import type { EditorState } from '../editorStore';

export type TourPlacement = 'right' | 'left' | 'bottom' | 'top' | 'center';

export interface TourStep {
  id: string;
  title: string;
  body: string;
  selector: string | null;
  placement: TourPlacement;
  advanceWhen?: (state: EditorState, startState: EditorState) => boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    body: "Let's build your first page in about two minutes",
    selector: null,
    placement: 'center'
  },
  {
    id: 'add-block',
    title: 'Add a block',
    body: 'Click a card to add it under the selected block. Start with a hero, text, image or contact section.',
    selector: '[data-tour="palette"]',
    placement: 'right',
    advanceWhen: (state, startState) => {
      const currentCount = state.site?.blocks.length ?? 0;
      const startCount = startState.site?.blocks.length ?? 0;
      return currentCount > startCount;
    }
  },
  {
    id: 'select-block',
    title: 'Click to select',
    body: 'Click any block on the canvas to select it. The inspector on the right will show its controls.',
    selector: '[data-tour="canvas"]',
    placement: 'right',
    advanceWhen: (state, startState) =>
      state.selectedBlockId !== null && startState.selectedBlockId === null
  },
  {
    id: 'edit-content',
    title: 'Edit the content',
    body: 'Use the right panel to change text, links and images. You can also click text directly on the page to edit it.',
    selector: '[data-tour="inspector"]',
    placement: 'left'
  },
  {
    id: 'rearrange',
    title: 'Rearrange',
    body: 'Open the Structure tab to drag blocks or use the arrows. Undo is always nearby if a move feels wrong.',
    selector: '[data-tour="tab-structure"]',
    placement: 'left'
  },
  {
    id: 'theme',
    title: 'Colours and fonts',
    body: 'Open the Theme tab to change colours, fonts, corner rounding and section spacing across the whole site.',
    selector: '[data-tour="tab-theme"]',
    placement: 'left'
  },
  {
    id: 'media',
    title: 'Add your images',
    body: 'Upload images in the Media tab, then pick them for any image block or visual section.',
    selector: '[data-tour="tab-media"]',
    placement: 'left'
  },
  {
    id: 'devices',
    title: 'Check every screen size',
    body: 'Use the device switcher to preview desktop, tablet and phone widths. Make sure buttons and images stay comfortable on small screens.',
    selector: '[data-tour="device"]',
    placement: 'bottom'
  },
  {
    id: 'undo',
    title: 'Undo is always there',
    body: 'Made a change you do not like? The Undo button lets you step back without starting over.',
    selector: '[data-tour="undo"]',
    placement: 'bottom'
  },
  {
    id: 'publish',
    title: 'Share & export',
    body: 'Share a preview link with anyone, or download your website as a ZIP to host anywhere. Want us to take it live for you? See our plans.',
    selector: '[data-tour="tab-publish"]',
    placement: 'left'
  }
];

/** The tour for designed templates: one page you restyle, with side tabs instead of a block palette. */
export const DESIGNED_TOUR_STEPS: TourStep[] = [
  {
    id: 'd-welcome',
    title: 'Make it yours',
    body: 'This is your site. Everything on it can be changed, and it takes about a minute to see how.',
    selector: null,
    placement: 'center'
  },
  {
    id: 'd-text',
    title: 'Click to edit',
    body: 'Click any text on the page and just type. Press Enter to save. Click a picture or a link to replace it.',
    selector: '[data-tour="dw-preview"]',
    placement: 'right'
  },
  {
    id: 'd-items',
    title: 'Menus, prices and lists',
    body: 'Lists such as menus, products, team members and prices live in Items. You can also click a card on the page to jump straight to it.',
    selector: '[data-tour="dw-tab-items"]',
    placement: 'left'
  },
  {
    id: 'd-images',
    title: 'Your own pictures',
    body: 'Swap any picture or logo for your own in Images. Use Locate to see exactly which one you are changing.',
    selector: '[data-tour="dw-tab-images"]',
    placement: 'left'
  },
  {
    id: 'd-colours',
    title: 'Colours and fonts',
    body: 'Change the whole colour scheme and the fonts in a couple of clicks. Everything updates live.',
    selector: '[data-tour="dw-tab-colours"]',
    placement: 'left'
  },
  {
    id: 'd-sections',
    title: 'Hide or reorder sections',
    body: 'Do not need a section? Hide it. Want it higher up the page? Move it. You can also add your own sections, such as prices, steps or questions.',
    selector: '[data-tour="dw-tab-sections"]',
    placement: 'left'
  },
  {
    id: 'd-devices',
    title: 'Check every screen',
    body: 'Switch between desktop, tablet and phone to see how your site looks everywhere.',
    selector: '[data-tour="device"]',
    placement: 'bottom'
  },
  {
    id: 'd-share',
    title: 'Share or take it with you',
    body: 'Share a preview link, or download your site as a ZIP to host anywhere. Undo is always one click away.',
    selector: '[data-tour="dw-tab-publish"]',
    placement: 'left'
  }
];
